import { useNavigate } from "@tanstack/react-router"
import { useState } from "react"
import { z } from "zod"
import { useDebouncedValue } from "./useDebouncedValue"

const coerceToString = (v: unknown) => (Array.isArray(v) ? v[0] : v)

export const SearchQuerySchema = z.object({
  query: z.preprocess(coerceToString, z.string()).optional().catch(undefined),
  lastRow: z.preprocess(coerceToString, z.string().nullish()).optional().catch(undefined),
})

type SearchQueryProps = {
  search: { query?: string }
  fullPath: string
  onSearchQueryChange?: (query?: string) => void
}

const DELAY_IN_SEARCH = 500

export function useSearchQuery({ search, fullPath, onSearchQueryChange }: SearchQueryProps) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const navigate = useNavigate({ from: fullPath as any })
  const { query: searchQuery } = search
  const [value, setValue] = useState(searchQuery)

  function setQuery(query: string | undefined) {
    navigate({
      // TODO: Remove this once the type is fixed
      search: ((previous: typeof search) => {
        const newSearch = { ...previous }

        if (!query || query.length === 0) {
          delete newSearch.query
        } else {
          newSearch.query = query
        }

        return newSearch
      }) as any, // eslint-disable-line @typescript-eslint/no-explicit-any
    })
  }

  const debouncedQuery = useDebouncedValue({
    value,
    delayInMs: DELAY_IN_SEARCH,
    callback: (value) => {
      // The debounce also fires on mount; navigating then drops child routes such as side panels.
      if (value === searchQuery) return

      onSearchQueryChange?.(value)
      setQuery(value)
    },
  })

  return {
    searchQuery: value,
    query: debouncedQuery,
    setQuery: setValue,
  }
}
